import React from 'react';
import { Play, ArrowRight } from '@piiqtechnologies/svg-icons';
import { styled, PeekBox, PeekTypography } from '@piiqtechnologies/ui-components';
import { changeTypeOfTags } from '../../constants.js';
import { formatNotificationReleaseDate } from '../../dates.js';
import { getWhatsNewDialog, getSlideWords, fullHeadlineOf } from '../../dialogs/catalog.js';
import { getNotificationEntryLinkLabel } from '../../entryLink.js';
import { NotificationChip, NotificationChipKind } from '../notification-chip/NotificationChip.js';

// One announcement in the What's New list (appendix §5.5), as the product design draws it (Figma
// "What's new drawer"): a status chip and the release date, the headline and description (the
// announcement's own, or else its pop-up's), and a link. A click on the entry opens its pop-up; the
// link goes straight where it says. Presentation only; exported on its own so the admin preview can
// show one.
// Buttons don't inherit the page's font, so the theme's is set here.
const LinkButton = styled("button")(({ theme }) => (Object.assign(Object.assign({}, theme.typography.h4), { display: "inline-flex", alignItems: "center", gap: 6, padding: 0, border: 0, background: "none", cursor: "pointer", textAlign: "left", color: theme.palette.secondary.main, fontWeight: theme.typography.fontWeightMedium, 
    // Above the headline's stretched click area, so it keeps its own action.
    position: "relative", zIndex: 1, "& svg": { width: 14, height: 14, flexShrink: 0 }, "&:focus-visible": { outline: `2px solid ${theme.palette.secondary.main}`, outlineOffset: 2 } })));
// The headline opens the pop-up. Its ::after stretches over the whole entry, so a click anywhere on
// it does too, and the focus ring goes round the entry.
const OpenButton = styled("button")(({ theme }) => ({
    font: "inherit",
    color: "inherit",
    textAlign: "left",
    padding: 0,
    border: 0,
    background: "none",
    cursor: "pointer",
    "&::after": { content: '""', position: "absolute", inset: 0, borderRadius: "inherit" },
    "&:focus-visible": { outline: "none" },
    "&:focus-visible::after": { outline: `2px solid ${theme.palette.secondary.main}`, outlineOffset: -2 },
}));
/** "New" or "Updated" (as its tags say) until the customer has opened it, then "Viewed". */
const chipOf = ({ isRead }, changeType) => {
    if (isRead)
        return NotificationChipKind.Viewed;
    return changeType === "improved" ? NotificationChipKind.Updated : NotificationChipKind.New;
};
const Icon = ({ children }) => (React.createElement(PeekBox, { component: "span", "aria-hidden": true, sx: { display: "flex" } }, children));
const NotificationEntry = ({ item, onClick, onOpen, bordered = false, changeType }) => {
    var _a;
    const dialog = getWhatsNewDialog(item.template);
    // A pop-up this build doesn't have has no headline to show; the list leaves it out too.
    if (!dialog)
        return null;
    const words = getSlideWords(item, dialog);
    const headline = fullHeadlineOf(words);
    const chip = chipOf(item, changeType || changeTypeOfTags(item.tags) || dialog.changeType);
    const isPreview = !onClick;
    const isDemo = ((_a = item.cta) === null || _a === void 0 ? void 0 : _a.variant) === "watch_demo";
    const linkLabel = getNotificationEntryLinkLabel(item);
    return (React.createElement(PeekBox, { "data-testid": "notification-entry", sx: Object.assign(Object.assign({ display: "flex", flexDirection: "column", gap: "8px", px: "24px", py: "20px", backgroundColor: "common.white" }, (bordered && { border: "1px solid", borderColor: "grey.200", borderRadius: "8px" })), (onOpen && {
            position: "relative",
            cursor: "pointer",
            transition: "background-color 150ms ease",
            "&:hover": { backgroundColor: "grey.50" },
        })) },
        React.createElement(PeekBox, { sx: { display: "flex", alignItems: "center", gap: "8px" } },
            React.createElement(PeekBox, { sx: { flex: 1, minWidth: 0, display: "flex" } },
                React.createElement(NotificationChip, { kind: chip, "data-testid": "notification-entry-chip" })),
            React.createElement(PeekTypography, { variant: "h4", sx: { color: "grey.400", whiteSpace: "nowrap" } }, formatNotificationReleaseDate(item.releaseAt))),
        React.createElement(PeekTypography, { variant: "h3", sx: { color: "text.primary", fontWeight: "fontWeightMedium" } }, onOpen ? (React.createElement(OpenButton, { type: "button", "data-testid": "notification-entry-open", onClick: () => onOpen(item) }, headline)) : (headline)),
        React.createElement(PeekTypography, { variant: "h4", sx: { color: "text.primary" } }, words.description),
        linkLabel && (React.createElement(PeekBox, { sx: { display: "flex", alignItems: "center", gap: "12px", pt: "4px" } },
            React.createElement(LinkButton, { type: "button", "data-testid": "notification-entry-link", tabIndex: isPreview ? -1 : undefined, onClick: () => onClick === null || onClick === void 0 ? void 0 : onClick(item) },
                isDemo && (React.createElement(Icon, null,
                    React.createElement(Play, null))),
                React.createElement(PeekBox, { component: "span", sx: { textDecoration: "underline", textUnderlineOffset: "2px" } }, linkLabel),
                !isDemo && (React.createElement(Icon, null,
                    React.createElement(ArrowRight, null))))))));
};

export { NotificationEntry, NotificationEntry as default };
